/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';

import {fromEvent, Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';

import { BarChart, GaugeChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, MarkLineComponent, MarkPointComponent, TitleComponent, ToolboxComponent, TooltipComponent } from 'echarts/components';
// import * as echarts from 'echarts/lib/echarts';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {Destroyable} from '../../../destroyable.component';
import {isPresent} from '../../utils/lang';

/**
 * A datum element in a data serie.
 */
export interface Datum {
  value: number;
  name?: string;
  color?: string;
  key?: string;

  [other: string]: any;
}

/**
 * A serie in a Echart chart
 */
export interface Serie {
  name: string;
  type: string;
  radius?: Array<string>;
  center?: Array<string>;
  data: Array<Datum> | Array<number>;
}

/**
 * Echarts interactive chart component.
 *
 * See http://echarts.baidu.com
 */
@Component({
  selector: 'gp-echart',
  template: `
    <div #chart class='echart-container' [style.height]="height" [style.width]="width"></div>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EChartComponent extends Destroyable implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('chart', {static: true})
  public chartContainer;

  @Input()
  public options: any;

  @Input()
  public width: string = '500px';

  @Input()
  public height: string = '300px';

  @Input()
  private colors: Array<string> = [
    '#5FB3EF',
    '#B19ED8',
    '#f44455',
    '#fcc100',
    '#0cc2aa',
    '#2e3e4e',
    '#a88add',
    '#6887ff',
    '#6cc788',
    '#f77a99',
    '#f44455',
    '#2EC7C9',
    '#f8f8f8'
  ];

  @Input()
  private dataClicked: (datum: Datum) => void;

  @Output()
  private datumClick: EventEmitter<Datum> = new EventEmitter<Datum>();

  @Output()
  private afterInit: EventEmitter<any> = new EventEmitter<any>();

  private chart: any;

  private resizeObserver: ResizeObserver;

  private isHidden: boolean = false;

  constructor(private elementRef: ElementRef, private zone: NgZone) {
    super();
    // Register required components
    echarts.use([
      LineChart,
      BarChart,
      PieChart,
      GaugeChart,
      TitleComponent,
      TooltipComponent,
      LegendComponent,
      GridComponent,
      MarkLineComponent,
      MarkPointComponent,
      ToolboxComponent,
      CanvasRenderer
    ]);
  }

  public ngAfterViewInit(): any {
    this.zone.runOutsideAngular(() => {
      this.chart = echarts.init(this.elementRef.nativeElement.querySelector('.echart-container'));
    });

    this.chart.on('click', this.onChartClick.bind(this));
    this.render();
    this.watchResize();
    this.afterInit.emit(this.chartContainer.nativeElement.getElementsByTagName('canvas')[0]);
  }

  public ngOnChanges(): any {
    if (isPresent(this.chart)) {
      this.render();
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    if (isPresent(this.resizeObserver)) {
      this.resizeObserver.disconnect();
    }
    if (isPresent(this.chart)) {
      this.chart.off('click', this.onChartClick.bind(this));
      this.chart.clear();
    }
  }

  private watchResize(): void {
    const resized$: Subject<void> = new Subject<void>();

    resized$
      .pipe(
        debounceTime(400),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.resizeChart());

    if (typeof ResizeObserver === 'undefined') {
      fromEvent(window, 'resize')
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(() => resized$.next());

      return;
    }

    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        const {width, height} = entries[0].contentRect;
        const wasHidden: boolean = this.isHidden;
        this.isHidden = !width || !height;

        if (this.isHidden) {
          return;
        }

        if (wasHidden) {
          this.resizeChart();
        } else {
          resized$.next();
        }
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    });
  }

  private resizeChart(): void {
    if (isPresent(this.chart)) {
      this.chart.resize();
    }
  }

  private render(): void {
    if (this.options !== undefined) {
      this.chart.setOption(this.options);

      if (!this.options.color || !this.options.color.length) {
        this.chart.setOption({
          color: this.colors
        });
      }
    }
  }

  private onChartClick(serieData: any): void {
    if (serieData.data && typeof serieData.data !== 'object') {
      serieData.data = {
        value: serieData.value,
        name: serieData.name
      };
    }
    if (this.dataClicked !== undefined) {
      this.dataClicked.call(null, serieData.data);
    }
    this.datumClick.emit(<Datum>serieData.data);
  }
}
